<template>
  <v-dialog
    :value="dialog"
    width="700"
    @click:outside="close"
    @keydown.esc="close"
  >
    <v-card class="py-4" height="100%">
      <v-card-title class="px-6 py-2 mb-4">
        {{ $t("events.bins-integration") }}

        <v-btn absolute right top icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form @submit.prevent="handleFormSubmit" ref="form">
          <v-row>
            <v-col cols="12">
              <BaseTextField
                v-model="description"
                :rules="[rules.required]"
                :label="$t('events.description')"
                :placeholder="$t('events.insert-description')"
                hide-details
                required
              ></BaseTextField>
            </v-col>

            <v-col cols="12">
              <v-file-input
                v-if="!discountId"
                v-model="file"
                @change="handleFileInput"
                @click:clear="binsSheet = null"
                :rules="[rules.required]"
                :disabled="isFileInputDisabled"
                prepend-icon=""
                accept=".xlsx,.xls,.csv"
                :label="$t('events.sheet')"
                append-icon="mdi-upload"
                :placeholder="$t('events.select-sheet')"
                class="rounded-lg required"
                color="fourth_darken"
                hide-details
                small-chips
                chips
                outlined
                dense
              >
              </v-file-input>

              <v-btn
                v-else
                @click="downloadBinsSheet"
                width="100%"
                class="rounded-lg"
                elevation="1"
              >
                <v-icon class="mr-2"> mdi-download-outline </v-icon>
                {{ $t("events.download-sheet") }}
              </v-btn>
            </v-col>

            <v-col cols="12" v-if="ticketsOptions.length > 0">
              <BaseAutocomplete
                v-model="tickets"
                :items="ticketsOptions"
                :rules="[rules.required]"
                item-text="descricao"
                item-value="idproduto_variacao"
                :label="$t('events.tickets')"
                :placeholder="$t('events.select-tickets')"
                :no-data-text="$t('events.no-results-found')"
                multiple
                hide-details
                required
              ></BaseAutocomplete>
            </v-col>

            <v-col cols="12">
              <BaseAutocomplete
                v-model="bank"
                :items="$config.bancos.result"
                :rules="[rules.required]"
                :label="$t('events.bank')"
                :placeholder="$t('events.select-bank')"
                :no-data-text="$t('events.no-results-found')"
                item-text="banco_nome"
                item-value="banco_codigo"
                hide-details
                required
              ></BaseAutocomplete>
            </v-col>

            <v-col cols="12" class="pt-0">
              <v-radio-group
                v-model="discountType"
                :rules="[rules.required]"
                :label="$t('events.discount-type') + ':'"
                hide-details
                row
              >
                <v-radio
                  :label="$t('events.event-config.value')"
                  value="value"
                ></v-radio>
                <v-radio
                  :label="$t('events.event-config.percentage')"
                  value="percentage"
                ></v-radio>
              </v-radio-group>
            </v-col>

            <v-col cols="12" sm="6" align="center">
              <BaseMoneyField
                v-if="discountType === 'value'"
                v-model="discountValue"
                :rules="[rules.required]"
                :options="options"
                :label="$t('events.discount-value')"
                :placeholder="$t('events.insert-discount-value')"
                hide-details
                required
              ></BaseMoneyField>
              <BaseTextField
                v-else
                v-model="discountPercentage"
                :rules="[rules.required]"
                type="number"
                :label="$t('events.discount-percentage')"
                :placeholder="$t('events.insert-discount-percentage')"
                prefix="%"
                hide-details
                required
              ></BaseTextField>
            </v-col>

            <v-col cols="12" sm="6">
              <BaseSelect
                v-model="status"
                :items="statusOptions"
                :rules="[rules.required]"
                label="Status"
                :placeholder="$t('events.select-status')"
                :no-data-text="$t('events.no-results-found')"
                item-text="title"
                item-value="value"
                hide-details
                required
              ></BaseSelect>
            </v-col>

            <v-col cols="12" sm="6">
              <BaseTextField
                v-model="initDateTime"
                :label="$t('events.initial-date')"
                type="datetime-local"
                max="9999-12-31T23:59"
              ></BaseTextField>
            </v-col>

            <v-col cols="12" sm="6">
              <BaseTextField
                v-model="endDateTime"
                :label="$t('events.final-date')"
                type="datetime-local"
                max="9999-12-31T23:59"
              ></BaseTextField>
            </v-col>

            <v-col class="d-flex justify-end">
              <BaseButton
                variant="filter"
                class="rounded-xxl ml-2"
                type="submit"
              >
                {{ buttonText }}
              </BaseButton>
            </v-col>
          </v-row>
        </v-form>

        <v-data-table
          :headers="headers"
          :items="binsData"
          :loading="binsLoading"
          class="mt-4"
          :no-data-text="$t('events.no-results-found')"
          :loading-text="$t('events.loading-info')"
          :items-per-page="5"
          :footer-props="{
            itemsPerPageOptions: [5, 10, 15],
            itemsPerPageText: $t('events.discounts-per-page'),
          }"
        >
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :color="$config.dados.corinputs"
                  small
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="editBins(item)"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("events.edit") }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import dayjs from "dayjs";
import mixins from "@/mixins";
import BaseAutocomplete from "@/components/forms/BaseAutocomplete.vue";
import BaseButton from "@/components/forms/BaseButton.vue";
import BaseMoneyField from "@/components/forms/BaseMoneyField.vue";
import BaseTextField from "@/components/forms/BaseTextField.vue";
import BaseSelect from "@/components/forms/BaseSelect.vue";

const { formRules } = mixins;

export default {
  name: "ModalBins",
  mixins: [formRules],
  components: {
    BaseAutocomplete,
    BaseButton,
    BaseMoneyField,
    BaseSelect,
    BaseTextField,
  },
  data: () => ({
    binsSheet: null,
    discountId: "",
    description: "",
    tickets: null,
    bank: "",
    status: "",
    discountType: "value",
    discountValue: "",
    discountPercentage: "",
    initDateTime: "",
    endDateTime: "",
    file: null,
    options: {
      locale: "pt-BR",
      prefix: "R$",
      suffix: "",
      length: 9,
      precision: 2,
    },
  }),
  props: {
    dialog: {
      type: Boolean,
      default: true,
    },
    eventData: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters(["binsData", "binsLoading"]),
    statusOptions() {
      return [
        {
          title: this.$t("events.active"),
          value: "ATIVO",
        },
        {
          title: this.$t("events.inactive"),
          value: "INATIVO",
        },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("events.description"),
          align: "start",
          sortable: true,
          value: "descricao",
        },
        {
          text: "Status",
          align: "start",
          sortable: true,
          value: "status",
        },
        {
          text: this.$t("events.actions"),
          align: "center",
          sortable: false,
          value: "actions",
        },
      ];
    },
    formData() {
      return {
        idproduto_variacao: this.joinTickets || "",
        idproduto:
          this.ticketsOptions.length >= 1 ? "" : this.eventData.idproduto,
        desconto_valor: this.discountValue || 0,
        desconto_perc: this.discountPercentage || 0,
        bins: this.binsSheet,
        banco: this.bank,
        descricao: this.description,
        status: this.status,
        dthr_inicio: this.initDateTime
          ? dayjs(this.initDateTime).format("DD-MM-YYYY HH:mm")
          : "",
        dthr_fim: this.endDateTime
          ? dayjs(this.endDateTime).format("DD-MM-YYYY HH:mm")
          : "",
      };
    },
    discounTypes() {
      return [
        {
          text: "Valor",
          value: "value",
        },
        {
          text: "Percentual",
          value: "percentage",
        },
      ];
    },
    buttonText() {
      return this.discountId
        ? this.$t("events.edit-discount")
        : this.$t("events.add-discount");
    },
    ticketsOptions() {
      return this.eventData.variantes.variacoes_produto.map(variation => {
        return variation;
      });
    },
    joinTickets() {
      if (!this.tickets) return;
      return this.tickets.join(";");
    },
    isFileInputDisabled() {
      return this.discountId ? true : false;
    },
  },
  watch: {
    discountType() {
      this.discountType === "value"
        ? (this.discountPercentage = "")
        : (this.discountValue = "");
    },
  },
  methods: {
    ...mapActions(["getBins", "registerOrUpdateBins"]),
    handleFileInput(file) {
      if (file) {
        const reader = new FileReader();

        reader.readAsText(file);
        reader.onload = event => {
          const convertToArray = event.target.result
            .split("\n")
            .map(item => item.replace("\r", ""))
            .filter(item => /^\d+$/.test(item));

          this.binsSheet = convertToArray.join(";");
        };
      }
    },
    editBins(item) {
      this.discountId = item.iddesconto_bin;
      this.description = item.descricao;
      this.tickets = item.idproduto_variacao;
      this.bank = item.banco;
      this.status = item.status;
      this.discountType = item.desconto_perc ? "percentage" : "value";
      this.discountValue = item.desconto_valor;
      this.discountPercentage = item.desconto_perc;
      this.initDateTime = item.dthr_inicio;
      this.endDateTime = item.dthr_fim;
      this.binsSheet = item.bins;
    },
    downloadBinsSheet() {
      const rows = this.binsSheet.split(";");
      const csvContent = rows.join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "bins.csv");
      link.click();
    },
    handleFormSubmit() {
      if (!this.$refs.form.validate()) return;

      this.discountId
        ? this.registerOrUpdateBins({
            ...this.formData,
            iddesconto_bin: this.discountId,
            eventId: this.eventData.idproduto,
          })
        : this.registerOrUpdateBins({
            ...this.formData,
            bins: this.binsSheet,
            iddesconto_bin: "",
            eventId: this.eventData.idproduto,
          });

      this.$refs.form.reset();
      this.discountId = "";
      this.getBins(this.eventData.idproduto);
    },
    close() {
      this.$emit("close");
    },
  },
  mounted() {
    this.$config.getBancos();
    this.getBins(this.eventData.idproduto);
  },
};
</script>
